import { GameDocumentContext } from '../../../contexts/game-document';
import { useCallback, useContext, useEffect, useState } from 'react';
import Toolbar from '../toolbar';
import {
  AddResourceAsync,
  AddTaskContentAsync,
  AddTimerEntityAsync,
  DeleteResourceAsync,
  DeleteTaskAsync,
  GetResourceValue,
  GetTaskById,
  UpdateGameDocState,
  UpdateResourceAsync,
  UpdateTaskAsync
} from '../../../utils/game-document';
import { uuid } from '../../../types/common-helper';
import { Button } from '@progress/kendo-react-buttons';
import {
  matchPath,
  useLocation,
  useNavigate,
  useParams
} from 'react-router-dom';
import { TaskEditor } from '../../../features/game-document/tasks';
import { ResourceEntity, TaskEntity } from '../../../types/game-document/';
import { FluidForm } from '../../../components/forms';
import RequiredFields from '../../../types/required-fields';
import { TaskEvent } from '../../../types/game-document/entities/task';
import cloneDeep from 'lodash.clonedeep';
import {
  ERR_DUPLICATE_NAME_VALUE,
  ERR_INPUT_REQUIRED
} from '../../../constants/text';
import { UploadedImage } from '../../../features/game-document/image-resource/resource-window';
import { useGameDocumentResources } from '../../../hooks/use-game-document-resources';
import { useToggle } from 'usehooks-ts';
import YesNoDialog from '../../../components/dialogs/yes-no-dialog';
import { useBreadcrumb } from '../../../contexts/breadcrumb';
import { fromLonLat, toLonLat } from 'ol/proj';

const Task = () => {
  const { taskId } = useParams();
  const [state, setState] = useContext(GameDocumentContext);
  const navigate = useNavigate();
  const resourceFiles = useGameDocumentResources();
  const getEntityById = (entityId: string) =>
    state.gameDocument?.assets.tasks?.find((i) => i.id === entityId)!;

  const [entity, setEntity] = useState<TaskEntity | undefined>();
  const [, setTitleResource] = useState<ResourceEntity>();
  const [, setImageResource] = useState<ResourceEntity>();
  const [, setCompleteImageResource] = useState<ResourceEntity>();
  const [resources, setResources] = useState<ResourceEntity[]>(
    [] as ResourceEntity[]
  );

  const [showConfirmationDelete, setShowConfirmationDelete] = useToggle(false);

  const [requiredFields, setRequiredFields] = useState<
    RequiredFields<TaskEntity>[]
  >([{ name: 'titleResId', errorMessage: '' }]);

  const onDeleteEntity = async (entityId: string) => {
    DeleteTaskAsync(state.gameDocument!, entityId).then(
      (updatedGameDocument) => {
        //delete annotation that assosiate with this deleted task
        const routes = cloneDeep(updatedGameDocument.assets.routes ?? []);
        const filtered = routes.filter(
          (r) => !(r.fromTaskId === entityId || r.toTaskId === entityId)
        );
        updatedGameDocument.assets.routes = filtered;

        setState((state) => UpdateGameDocState(state, updatedGameDocument));
        navigate('../tasks');
      }
    );
  };

  const handleTaskResourceUpload = useCallback(
    (field: keyof TaskEntity, upload: UploadedImage) => {
      if (!entity || !state.gameDocument) return;

      const defaultTaskAvailableIconResId =
        state.gameDocument.settings.designer?.defaultTaskAvailableIconResId ||
        '';
      const defaultTaskCompletedIconResId =
        state.gameDocument.settings.designer?.defaultTaskCompletedIconResId ||
        '';

      const defaultResId =
        field === 'imageResId'
          ? defaultTaskAvailableIconResId
          : field === 'completeImageResId'
            ? defaultTaskCompletedIconResId
            : undefined;

      const currentResId = entity[field] as string;

      if (defaultResId && currentResId === defaultResId) {
        // If it is the default icon, create a NEW custom resource in the game doc
        const newResId = uuid();

        AddResourceAsync(
          state.gameDocument,
          upload.name ?? upload.fileName ?? '',
          upload.fileName ?? '',
          'image',
          upload.blobUrl,
          newResId,
          upload.size,
          upload.category
        ).then((docWithResource) => {
          const updatedTask = {
            ...entity,
            [field]: newResId
          };

          UpdateTaskAsync(docWithResource, taskId!, updatedTask, false).then(
            (finalDoc) => {
              setState((prev) => UpdateGameDocState(prev, finalDoc));
              setEntity(updatedTask);
            }
          );
        });
      } else {
        // Update existing custom resource
        resourceFiles.updateResourceFile(currentResId, upload);
      }
    },
    [entity, resourceFiles, state.gameDocument, taskId, setState]
  );

  const handleTaskChange = useCallback(
    (
      field: keyof TaskEntity,
      value:
        | boolean
        | string
        | TaskEvent
        | { enablePremessageOutsideRange?: boolean; setTaskProximity?: number }
        | undefined
    ) => {
      if (!entity) return;
      setRequiredFields((prev) =>
        prev.map((task: RequiredFields<TaskEntity>) => {
          const copyTask = cloneDeep({ ...task });
          if (task.name === field) {
            copyTask.errorMessage = '';
            if (field === 'titleResId') {
              if (value === '') {
                copyTask.errorMessage = ERR_INPUT_REQUIRED;
              }
            }
          }

          return copyTask;
        })
      );

      const defaultTaskAvailableIconResId =
        state.gameDocument?.settings.designer?.defaultTaskAvailableIconResId;
      const defaultTaskCompletedIconResId =
        state.gameDocument?.settings.designer?.defaultTaskCompletedIconResId;

      if (
        field.toLowerCase().endsWith('resid') &&
        value !== defaultTaskAvailableIconResId &&
        value !== defaultTaskCompletedIconResId
      ) {
        setResources((prev) =>
          prev.map((resource: ResourceEntity) =>
            resource?.id === entity[field]
              ? { ...resource, value: value as string }
              : { ...resource }
          )
        );
        const resource = resources.find((item) => item.id === entity[field])!;
        if (resource) {
          resource.value = value as string;
          UpdateResourceAsync(state.gameDocument!, resource.id, resource).then(
            (response) => setState((prev) => UpdateGameDocState(prev, response))
          );
        }
      } else if (
        field.toLowerCase().endsWith('resid') &&
        (value === defaultTaskAvailableIconResId ||
          value === defaultTaskCompletedIconResId)
      ) {
        // Reverting back to default setting icon
        const updatedTask = {
          ...entity,
          [field]: value as string
        };
        setEntity(updatedTask);

        const oldResId = entity[field] as string;
        const deletePromise =
          oldResId &&
          oldResId !== defaultTaskAvailableIconResId &&
          oldResId !== defaultTaskCompletedIconResId
            ? DeleteResourceAsync(state.gameDocument!, oldResId)
            : Promise.resolve(state.gameDocument!);

        deletePromise.then((docWithDeletedResource) => {
          UpdateTaskAsync(
            docWithDeletedResource,
            taskId!,
            updatedTask,
            false
          ).then((response) =>
            setState((prev) => UpdateGameDocState(prev, response))
          );
        });
      } else if (field === 'events') {
        setEntity((prev) => ({ ...prev!, events: value as TaskEvent }));
        UpdateTaskAsync(
          state.gameDocument!,
          taskId!,
          {
            ...entity,
            events: value as TaskEvent
          },
          false
        ).then((response) =>
          setState((prev) => UpdateGameDocState(prev, response))
        );
      } else {
        let finalValue = value;

        if (field === 'taskContentId' && value === '-') {
          const taskTitle = GetResourceValue(
            state.gameDocument!,
            entity.titleResId ?? ''
          );

          const taskContentId = uuid();
          const titleGuid = uuid();
          const preMessageGuid = uuid();
          const contentGuid = uuid();
          const addTitleResource = AddResourceAsync(
            state.gameDocument!,
            'Title',
            '',
            'text',
            `${taskTitle} - task content`,
            titleGuid
          );
          const addContentResource = AddResourceAsync(
            state.gameDocument!,
            'Content',
            '',
            'text-md',
            '',
            contentGuid
          );
          const addPreMessageResource = AddResourceAsync(
            state.gameDocument!,
            'PreMessage',
            '',
            'text-md',
            '',
            preMessageGuid
          );

          Promise.all([
            addTitleResource,
            addContentResource,
            addPreMessageResource
          ]).then(() => {
            AddTaskContentAsync(
              state.gameDocument!,
              `${taskTitle} - task content`,
              `${taskTitle} - task content`,
              'HTML',
              titleGuid,
              preMessageGuid,
              contentGuid,
              taskContentId
            ).then((updatedGameDocument) => {
              setState((state) =>
                UpdateGameDocState(state, updatedGameDocument)
              );
            });
          });

          finalValue = taskContentId;
        }

        if (field === 'timerAssId' && value === '-') {
          const taskTitle = GetResourceValue(
            state.gameDocument!,
            entity.titleResId ?? ''
          );

          const timerId = uuid();
          const titleResId = uuid();
          AddResourceAsync(
            state.gameDocument!,
            'Title',
            '',
            'text',
            `${taskTitle} - timer`,
            titleResId
          ).then(() => {
            AddTimerEntityAsync(state.gameDocument!, {
              id: timerId,
              name: `${taskTitle} - timer`,
              description: `${taskTitle} - timer`,
              titleResId: titleResId,
              direction: 'down',
              repeats: false,
              interval: 0
            }).then((updatedGameDocument) => {
              setState((state) =>
                UpdateGameDocState(state, updatedGameDocument)
              );
            });
          });

          finalValue = timerId;
        }

        const result = {
          ...entity,
          [field]: finalValue
        };

        if (
          field === 'taskContentId' &&
          result.events &&
          result.events.onCompleteTask
        ) {
          const onComplete = cloneDeep(result.events.onCompleteTask);
          onComplete.steps = (onComplete.steps ?? []).map((s) => {
            if (s.operation === 'choosesAnswer') s.argumentAssetId = '';
            return s;
          });

          result.events.onCompleteTask = onComplete;
        }

        setEntity(result);
        UpdateTaskAsync(state.gameDocument!, taskId!, result, false).then(
          (updatedGameDocument) => {
            setState((state) => UpdateGameDocState(state, updatedGameDocument));
          }
        );
      }
    },
    [taskId, state.gameDocument, entity, resources, setState]
  );

  const handleCoordinateSave = async (lat: number, lon: number) => {
    if (!entity || !state.gameDocument) return;
    const updatedTask = cloneDeep(entity);
    if (!updatedTask.boundary) return;
    updatedTask.boundary.geometry.coordinates = fromLonLat([lon, lat]);
    const result = await UpdateTaskAsync(
      state.gameDocument,
      taskId!,
      updatedTask,
      false
    );
    setState((prev) => UpdateGameDocState(prev, result));
  };

  useEffect(() => {
    if (!taskId || !state.gameDocument) return;
    const entity = getEntityById(taskId);
    if (!entity) return;

    // Set the page title.
    document.title = `${GetResourceValue(state.gameDocument, entity.titleResId ?? '')} - ${state.gameDocument?.name}`;
    const titleRes: ResourceEntity = state.gameDocument?.resources.find(
      (task) => task.id === entity.titleResId
    )!;
    const imageRes: ResourceEntity = state.gameDocument?.resources.find(
      (task) => task.id === entity.imageResId
    )!;
    const completeImageRes: ResourceEntity = state.gameDocument?.resources.find(
      (task) => task.id === entity.completeImageResId
    )!;
    setEntity(entity);
    setTitleResource(titleRes);
    setImageResource(imageRes);
    setCompleteImageResource(completeImageRes);
    setResources([titleRes, imageRes, completeImageRes]);
  }, [state.gameDocument, taskId]);

  const currentTaskIndex =
    state.gameDocument?.assets.tasks?.findIndex((task) => task.id === taskId) ??
    0;

  const handleNavigateTask = (type: 'prev' | 'next') => {
    if (typeof currentTaskIndex !== 'undefined') {
      const destinationTask = state.gameDocument?.assets.tasks?.at(
        currentTaskIndex + (type === 'next' ? 1 : -1)
      );
      if (destinationTask) {
        navigate(
          `/designer/${state.gameId}/assets/tasks/${destinationTask.id}`
        );
        setEntity(destinationTask);
      }
    }
  };

  const location = useLocation();
  const { setBreadcrumb } = useBreadcrumb();

  const getTaskTitle = (taskId: string | undefined) => {
    if (taskId) {
      const task = GetTaskById(state.gameDocument!, taskId);
      if (task) {
        return GetResourceValue(state.gameDocument!, task.titleResId ?? '');
      }
    }
  };

  useEffect(() => {
    if (!taskId || !state.gameDocument) return;

    const breadcrumbPaths = [
      { path: '/designer/:gameId/assets/*', text: 'Assets' },
      { path: '/designer/:gameId/assets/tasks/*', text: 'Tasks' },
      {
        path: '/designer/:gameId/assets/tasks/:taskId/*',
        text: getTaskTitle(taskId)
      }
    ];

    const newBreadcrumb = [];
    for (const config of breadcrumbPaths) {
      const match = matchPath(config.path, location.pathname);

      if (match) {
        newBreadcrumb.push({ text: config.text, to: match.pathnameBase });
      }
    }

    setBreadcrumb(newBreadcrumb);

    return () => {
      setBreadcrumb([]);
    };
  }, [setBreadcrumb, taskId, location.pathname, state.gameDocument]);

  if (!entity) return <></>;

  const taskCoords = entity?.boundary?.geometry?.coordinates
    ? toLonLat(entity.boundary.geometry.coordinates)
    : null;

  const taskCoordinates = taskCoords
    ? { lat: taskCoords[1], lon: taskCoords[0] }
    : undefined;

  return (
    <>
      <Toolbar
        title={GetResourceValue(state.gameDocument!, entity.titleResId ?? '')}
        buttonHelpSupport={{
          title: 'Task action & events guide',
          url: 'https://forum.catalystglobal.com/t/4529'
        }}>
        <Button
          onClick={setShowConfirmationDelete}
          themeColor={'error'}
          fillMode={'flat'}>
          <span className={'material-symbols-outlined'}>delete</span>
        </Button>
        <div
          className={'d-flex gap-2 pl-3'}
          style={{
            borderLeft: '1px solid #e9e9e9'
          }}>
          <Button
            onClick={() => handleNavigateTask('prev')}
            className={'me-1'}
            disabled={currentTaskIndex === 0}
            themeColor={'secondary'}
            fillMode={'outline'}>
            Previous
          </Button>

          <Button
            onClick={() => handleNavigateTask('next')}
            disabled={
              currentTaskIndex + 1 === state.gameDocument?.assets.tasks?.length
            }
            themeColor={'success'}
            fillMode={'solid'}>
            Next
          </Button>
        </div>
      </Toolbar>
      <FluidForm>
        <TaskEditor
          key={taskId}
          editorMode={'full'}
          navigationSource={'asset'}
          entity={entity}
          resources={resources!}
          imageResourceVisibility={true}
          handleTaskChange={handleTaskChange}
          handleTaskResourceUpload={handleTaskResourceUpload}
          requiredFields={requiredFields}
          taskCoordinates={taskCoordinates}
          onCoordinateSave={handleCoordinateSave}
        />
      </FluidForm>
      {showConfirmationDelete && (
        <YesNoDialog
          title={'Confirm removal'}
          onConfirm={(action) =>
            action === 'yes'
              ? onDeleteEntity(entity.id)
              : setShowConfirmationDelete()
          }
          onClose={() => setShowConfirmationDelete()}>
          Are you sure you want to remove the task?
        </YesNoDialog>
      )}
    </>
  );
};

export default Task;
